import React, { FC, useState } from 'react';
import { SectionWrapper } from '../../../components/SectionWrapper';
import { IModalWindow, ModalWindow } from '../../../components/modal/ModalWindow';
import { useTranslation } from 'react-i18next';
import { ButtonComponent } from '../../../components/ButtonComponent';
import { OwnerModalForm } from '../../../components/merchants/MerchantPage/OwnerModalForm';
import { TablesNames } from '../../../store/tables/tables-context';
import { Table } from '../../../components/table/Table';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { OwnerDetailPanel } from '../../../components/merchants/MerchantPage/OwnerDetailPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import {
	addOwnerData,
	deleteOwnerData,
	fetchMerchantData,
	setMerchantDetailsData,
} from '../../../store/merchnts/actions/merchant';
import { useParams } from 'react-router-dom';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { OwnerFieldReturnTypes } from '../../../constants/merchant/owner-form';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';

export const MerchantOwnerPage: FC = () => {
	const { t } = useTranslation();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const { id: mid } = useParams<{ id: string }>();
	const { merchantState: { merchantData, isMerchantDataLoading }, dispatch } = useMerchants();
	const [ownerModalState, setModalOwnerState] = useState<{ isOpen: boolean, ownerId?: string }>({
		isOpen: false,
		ownerId: '',
	});
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const closeOwnerModal = (): void => setModalOwnerState({ isOpen: false, ownerId: '' });

	const addNewOwnerBtn = (
		<ButtonComponent
			buttonName="Add"
			additionalClass="btn-sm"
			onButtonClick={(): void => setModalOwnerState({ isOpen: true })}
		/>
	);

	const ownerData = merchantData?.owners?.find(el => el?.id === ownerModalState.ownerId);

	const handleSuccess = (): void => {
		closeOwnerModal();
		setModalState({
      isOpen: true,
      modalTitle: 'Edit owner`s information',
      content: (
        <div className="fs-6">
          Owner`s information was edited successfully
        </div>
      ),
      confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
      minHeight: 100,
      onConfirm: async () => {
	      closeModal();
	      await fetchMerchantData(dispatch, mid);
      },
      onClose: closeModal
    });
	};

	const handleError = (): void => {
		closeOwnerModal();
		setModalState({
      isOpen: true,
      modalTitle: 'Edit owner`s information',
      content: (
        <div className="fs-6">
	        Failed saving owner`s information
        </div>
      ),
      confirmButtonText: t('PARTNERS.FAIL_BTN_NAME'),
      minHeight: 100,
      onConfirm: closeModal,
      onClose: closeModal
    });
	};

	const handleDelete = (ownerId?: string): void => {
		setModalState({
      isOpen: true,
      modalTitle: 'Delete owner`s information',
      modalText: 'Attention! Are you sure, that you want to delete owner`s information? This action cannot be undone.',
      onConfirm: async () => {
        await deleteOwnerData(dispatch, mid, ownerId);
	      closeModal();
      },
      onCancel: closeModal,
      onClose: closeModal,
      backdrop: 'static',
    });
	};

	const handleOwnerSubmit = async (data: OwnerFieldReturnTypes): Promise<void> => {
		if (ownerModalState.ownerId) {
			data.id = ownerData?.id;
			await setMerchantDetailsData(mid, data, MERCHANTS_TABS.OWNER);
			handleSuccess();
		} else {
			await addOwnerData(mid, data);
			await fetchMerchantData(dispatch, mid);
		}
	};

	const rowActions = (rowData): JSX.Element => {
		return (
			<div className="d-flex align-items-center justify-content-center">
				<div
					className="d-flex align-items-center justify-content-center cursor-pointer me-5"
					onClick={(): void => setModalOwnerState({ isOpen: true, ownerId: rowData?.original?.id })}
				>
					<FontAwesomeIcon icon={faPenToSquare} className="text-muted" />
					<span className="text-muted fs-7 fw-bolder ms-2">
						Edit
					</span>
				</div>
				<div
					className="d-flex align-items-center justify-content-center cursor-pointer"
					onClick={(): void => handleDelete(rowData?.original?.id) }
				>
					<FontAwesomeIcon icon={faTrashCan} className="text-muted" />
					<span className="text-muted fs-7 fw-bolder ms-2">
						Delete
					</span>
				</div>

			</div>
		);
	};

	const showPlaceholder = !merchantData || typeof merchantData === 'object' && Object.keys(merchantData).length === 0 || typeof merchantData !== 'object';

	return (
		<>
			<SectionWrapper
				showPlaceholder={showPlaceholder}
				title={t('MERCHANT.OWNER')}
				sectionBodyAdditionalClass="pb-0"
				pageLoading={false}
				isCardBody
				headerAdditionalBlock={checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && addNewOwnerBtn}
			>
				<Table
					name={TablesNames.merchantOwnerTableData}
					data={merchantData?.owners || []}
					isLoading={isMerchantDataLoading}
					renderDetailPanel={OwnerDetailPanel}
					enableRowActions={checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)}
					rowActions={rowActions}
					positionActionsColumn='last'
					actionColumnsSize={100}
				/>

			</SectionWrapper>

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

			<ModalWindow
				isOpen={ownerModalState.isOpen}
				onClose={closeOwnerModal}
				size="md"
				backdrop="static"
				modalTitle={ownerModalState.ownerId ? 'Edit owner' : 'Add new owner'}
				content={<OwnerModalForm
					ownerData={ownerData}
					ownerId={ownerModalState.ownerId}
					onSubmit={handleOwnerSubmit}
					onError={handleError}
					onClose={closeOwnerModal}
				/>}
			/>

		</>
	);
};
